/**
 * Supabase Authentication Context
 * 
 * Verwaltet den User-State für Supabase Auth und stellt Auth-Informationen
 * für die gesamte App bereit. Nutzt Supabase Client für Session Management.
 */

import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { getSupabaseClient } from '@/lib/supabase-client';
import { apiClient } from '@/lib/api-client';
import type { User } from '@/types';

// ===========================================
// TYPES
// ===========================================

interface SupabaseAuthContextType {
  /** Aktueller User (undefined wenn nicht geladen) */
  user: User | undefined;
  /** Lädt User-Daten? */
  isLoading: boolean;
  /** Ist authentifiziert? */
  isAuthenticated: boolean;
  /** Fehler beim Laden */
  error: Error | null;
  /** Access Token (für API Calls) */
  accessToken: string | null;
  /** Login mit Email/Password */
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  /** Registrierung */
  register: (email: string, password: string, username: string) => Promise<{ success: boolean; error?: string }>;
  /** Magic Link senden */
  sendMagicLink: (email: string) => Promise<{ success: boolean; error?: string }>;
  /** Magic Link verifizieren */
  verifyMagicLink: (token: string, email: string) => Promise<{ success: boolean; error?: string }>;
  /** Passwort-Reset Email senden */
  sendPasswordReset: (email: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  /** Logout */
  logout: () => Promise<void>;
  /** User-Daten neu laden */
  refetch: () => Promise<void>;
}

// ===========================================
// CONTEXT
// ===========================================

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(undefined);

// ===========================================
// PROVIDER
// ===========================================

interface SupabaseAuthProviderProps {
  children: ReactNode;
}

export function SupabaseAuthProvider({ children }: SupabaseAuthProviderProps) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  
  // Ref um aktuellen User in Callbacks zu tracken (verhindert stale closure)
  // Wird direkt in fetchUser() aktualisiert, nicht via useEffect (schneller)
  const userRef = useRef<User | undefined>(undefined);

  // User-Daten vom Backend holen
  const fetchUser = async (token: string | null = null): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Wenn Token vorhanden, nutze ihn für API Call
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await apiClient.get('/api/v2/auth/me', { headers });

      if (response.data.success && response.data.user) {
        console.log('[SupabaseAuthContext] fetchUser success:', response.data.user.email);
        const userData = response.data.user as User;
        // WICHTIG: userRef VOR setUser aktualisieren (verhindert doppelte Fetches)
        userRef.current = userData;
        setUser(userData);
      } else {
        userRef.current = undefined;
        setUser(undefined);
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      userRef.current = undefined;
      setUser(undefined);
      setError(err instanceof Error ? err : new Error('Failed to fetch user'));
    } finally {
      setIsLoading(false);
    }
  };

  // Supabase Session prüfen und User laden
  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;

    const initAuth = async () => {
      try {
        const supabase = await getSupabaseClient();

        // Prüfe aktuelle Session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          setError(sessionError);
          setIsLoading(false);
          return;
        }

        if (session?.access_token) {
          setAccessToken(session.access_token);
          await fetchUser(session.access_token);
        } else {
          setIsLoading(false);
        }

        // Listener für Auth-Änderungen
        const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('[SupabaseAuthContext] onAuthStateChange:', event, 'user loaded:', !!userRef.current);
            
            if (event === 'SIGNED_IN' && session?.access_token) {
              setAccessToken(session.access_token);
              // Nur fetchUser wenn noch kein User geladen ist
              // (vermeidet doppelten Fetch nach login())
              // Nutze userRef.current statt user (verhindert stale closure)
              if (!userRef.current) {
                await fetchUser(session.access_token);
              } else {
                setIsLoading(false);
              }
            } else if (event === 'SIGNED_OUT') {
              setAccessToken(null);
              setUser(undefined);
              setIsLoading(false);
            } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
              setAccessToken(session.access_token);
            }
          }
        );

        subscription = authSubscription;
      } catch (err) {
        console.error('Auth init error:', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize auth'));
        setIsLoading(false);
      }
    };

    initAuth();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Login mit Email/Password
  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      
      const response = await apiClient.post('/api/v2/auth/login', {
        email,
        password,
      });

      if (response.data.success && response.data.session?.access_token) {
        const token = response.data.session.access_token;
        setAccessToken(token);
        
        // Vollständige User-Daten vom Backend holen (mit explizitem Token Header)
        // fetchUser() setzt den Token als Authorization Header, daher klappt das
        await fetchUser(token);
        
        // Supabase Session setzen (async, NICHT blockierend!)
        // setSession() kann sehr langsam sein auf Windows, daher fire-and-forget
        getSupabaseClient().then(supabase => {
          supabase.auth.setSession({
            access_token: token,
            refresh_token: response.data.session.refresh_token || '',
          }).catch(err => console.warn('setSession warning:', err));
        });

        return { success: true };
      } else {
        setIsLoading(false);
        return { success: false, error: response.data.error || 'Login failed' };
      }
    } catch (err: unknown) {
      setIsLoading(false);
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = error.response?.data?.error || error.message || 'Login failed';
      return { success: false, error: errorMessage };
    }
  };

  // Registrierung
  const register = async (email: string, password: string, username: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.post('/api/v2/auth/register', {
        email,
        password,
        username,
      });

      if (response.data.success) {
        return { success: true };
      } else {
        return { success: false, error: response.data.error || 'Registration failed' };
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = error.response?.data?.error || error.message || 'Registration failed';
      return { success: false, error: errorMessage };
    }
  };

  // Magic Link senden
  const sendMagicLink = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.post('/api/v2/auth/magic-link', {
        email,
      });

      if (response.data.success) {
        return { success: true };
      } else {
        return { success: false, error: response.data.error || 'Failed to send magic link' };
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send magic link';
      return { success: false, error: errorMessage };
    }
  };

  // Magic Link verifizieren
  const verifyMagicLink = async (token: string, email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      
      const response = await apiClient.post('/api/v2/auth/verify-magic-link', {
        token,
        email,
      });

      if (response.data.success && response.data.session?.access_token) {
        const accessTokenValue = response.data.session.access_token;
        setAccessToken(accessTokenValue);
        
        // Vollständige User-Daten vom Backend holen (mit explizitem Token Header)
        await fetchUser(accessTokenValue);
        
        // Supabase Session setzen (async, NICHT blockierend!)
        getSupabaseClient().then(supabase => {
          supabase.auth.setSession({
            access_token: accessTokenValue,
            refresh_token: response.data.session.refresh_token || '',
          }).catch(err => console.warn('setSession warning:', err));
        });

        return { success: true };
      } else {
        setIsLoading(false);
        return { success: false, error: response.data.error || 'Verification failed' };
      }
    } catch (err: unknown) {
      setIsLoading(false);
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = error.response?.data?.error || error.message || 'Verification failed';
      return { success: false, error: errorMessage };
    }
  };

  // Passwort-Reset Email senden
  const sendPasswordReset = async (email: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const response = await apiClient.post('/api/v2/auth/reset-password', {
        email,
      });

      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send reset email';
      return { success: false, error: errorMessage };
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    try {
      const supabase = await getSupabaseClient();
      await supabase.auth.signOut();
      setAccessToken(null);
      userRef.current = undefined;
      setUser(undefined);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const value: SupabaseAuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user && !!accessToken,
    error,
    accessToken,
    login,
    register,
    sendMagicLink,
    verifyMagicLink,
    sendPasswordReset,
    logout,
    refetch: () => fetchUser(accessToken),
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
}

// ===========================================
// HOOK
// ===========================================

// eslint-disable-next-line react-refresh/only-export-components -- context hook must be co-located with provider
export function useSupabaseAuth(): SupabaseAuthContextType {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth muss innerhalb von SupabaseAuthProvider verwendet werden');
  }
  return context;
}

export default SupabaseAuthContext;
